function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import * as api from "./sequences-api";

/*
 * Sequences, the render side (PlunkDocs specs/SEQUENCES.md §13.3, mockup sections 3 and 4) plus the Assets tab list.
 *  - RenderProgress: one card per format of the batch in flight, progress and stage from GET renders/{id} polling.
 *  - Outputs: the newest finished render per format: player, duration and size, "older than the storyboard" when
 *    scriptVersion lags, downloads, Copy link, Attach to piece when the sequence has a container, and the hooks the
 *    clip workspace offers any video (Cut and caption, Trim, Find clips, Resize for channels), via "Edit in clip workspace".
 *  - RendersTable: every render on the sequence, newest first, with Retry for a failed format.
 *  - SequencesList: the Assets tab entry point (GET /sequences summaries) that opens the storyboard.
 */

const toast = api.toast;
const STAGE_TEXT = {
  prep: "preparing media",
  narration: "narration",
  assemble: "assembling",
  music: "music",
  upload: "uploading",
  queued: "waiting for a worker"
};
const stageText = r => {
  if (!r) return "";
  if (r.status === "queued") return STAGE_TEXT.queued;
  if (r.status === "done") return "uploaded";
  if (r.status === "failed") return "failed";
  if (r.status === "cancelled") return "cancelled";
  const s = String(r.stage || "").toLowerCase();
  return STAGE_TEXT[s] || r.stage || "rendering";
};
const elapsed = r => {
  if (!r) return "";
  const a = r.startedDate || r.createdDate;
  if (!a) return "";
  const b = r.finishedDate ? new Date(r.finishedDate) : new Date();
  const s = Math.max(0, Math.round((b - new Date(a)) / 1000));
  if (isNaN(s)) return "";
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} m ${String(s % 60).padStart(2, "0")} s`;
};
const statusChip = r => {
  const s = r.status;
  const cls = s === "done" ? " ok" : s === "failed" ? " bad" : s === "rendering" ? " info" : s === "cancelled" ? " warn" : "";
  return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip" + cls
  }, s === "done" ? "done" : s);
};
export const latestBatch = renders => {
  if (!renders || !renders.length) return [];
  const sorted = [...renders].sort((a, b) => String(b.createdDate || "").localeCompare(String(a.createdDate || "")));
  const bid = sorted[0].batchId;
  return bid ? sorted.filter(r => r.batchId === bid) : [sorted[0]];
};
export const latestDonePerFormat = (renders, formats) => (formats || []).map(f => (renders || []).filter(r => r.format === f && r.status === "done" && r.url).sort((a, b) => String(b.finishedDate || b.createdDate || "").localeCompare(String(a.finishedDate || a.createdDate || "")))[0] || null);
export const copyText = (t, msg) => {
  try {
    navigator.clipboard.writeText(t);
    toast(msg || "Link copied.");
  } catch (e) {
    toast("Could not copy: select the link and copy it.");
  }
};
export const assetOf = (r, seq) => r && r.assetId ? {
  id: r.assetId,
  url: r.url,
  posterUrl: r.posterUrl,
  durationSeconds: r.durationSeconds,
  title: `${seq && seq.title || "Sequence"} · ${r.format}`,
  kind: "video"
} : null;

/** Per-format progress for the batch in flight (or the one that just finished). */
export function RenderProgress({
  renders,
  onRetry,
  busy
}) {
  const batch = latestBatch(renders);
  if (!batch.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "sq-rend"
  }, batch.map(r => /*#__PURE__*/React.createElement("div", {
    key: r.id,
    className: "sq-rcard " + r.status
  }, /*#__PURE__*/React.createElement("div", {
    className: "h"
  }, /*#__PURE__*/React.createElement("b", null, r.format), statusChip(r), /*#__PURE__*/React.createElement("span", {
    className: "territory-hint",
    style: {
      marginLeft: "auto"
    }
  }, r.status === "done" ? `${api.fmtClock(r.durationSeconds)}${r.width ? ` · ${r.width} x ${r.height}` : ""}` : stageText(r))), /*#__PURE__*/React.createElement("div", {
    className: "sq-progress"
  }, /*#__PURE__*/React.createElement("i", {
    style: {
      width: `${r.status === "done" ? 100 : r.progress}%`
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "st"
  }, /*#__PURE__*/React.createElement("span", null, r.status === "failed" ? "" : r.status === "done" ? "uploaded" : r.stage || stageText(r)), /*#__PURE__*/React.createElement("span", null, elapsed(r))), r.status === "failed" ? /*#__PURE__*/React.createElement("div", {
    className: "sq-err"
  }, r.format, " failed", r.stage ? ` at ${r.stage}` : "", r.error ? `: ${String(r.error).slice(0, 220)}` : ".", onRetry ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs",
    disabled: busy,
    onClick: () => onRetry([r.format])
  }, "Retry this format") : null) : null)));
}

/** The newest finished output per format, the send-to row and the hooks. */
export function Outputs({
  sequence,
  renders,
  onRetry,
  onAttach,
  onHook,
  busy,
  attachBusy
}) {
  const outs = latestDonePerFormat(renders, sequence.formats).filter(Boolean);
  const extra = (renders || []).filter(r => r.status === "done" && r.url && !sequence.formats.includes(r.format)).reduce((m, r) => m.some(x => x.format === r.format) ? m : [...m, r], []);
  const list = [...outs, ...extra];
  if (!list.length) return /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      margin: "8px 0"
    }
  }, "No finished render yet. Render from the storyboard; each format lands here as it finishes.");
  const stale = r => sequence.scriptVersion && r.scriptVersion && r.scriptVersion < sequence.scriptVersion;
  const hooks = [["edit", "movie_edit", "Edit", "Video Edit Mode: cuts, captions, end card"], ["trim", "content_cut", "Trim", "Trim or extend"], ["clips", "auto_awesome", "Clips", "Find clips"], ["resize", "aspect_ratio", "Resize", "Resize for channels"]];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "sq-outs"
  }, list.map(r => /*#__PURE__*/React.createElement("div", {
    key: r.id,
    className: "sq-ocard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "fr",
    style: {
      aspectRatio: api.aspectOf(r.format)
    }
  }, /*#__PURE__*/React.createElement("video", {
    src: r.url,
    poster: r.posterUrl || undefined,
    controls: true,
    playsInline: true,
    preload: "metadata"
  })), /*#__PURE__*/React.createElement("div", {
    className: "meta"
  }, /*#__PURE__*/React.createElement("b", null, r.format, r.width ? ` · ${r.width} x ${r.height}` : ` · ${api.formatDims(r.format)}`), /*#__PURE__*/React.createElement("span", null, api.fmtClock(r.durationSeconds), r.finishedDate ? ` · ${api.fmtDate(r.finishedDate)}` : "", " \xB7 v", r.scriptVersion || "?"), stale(r) ? /*#__PURE__*/React.createElement("span", {
    className: "plk-chip warn",
    title: `Rendered from script v${r.scriptVersion}; the storyboard is at v${sequence.scriptVersion}`
  }, "older than the storyboard") : /*#__PURE__*/React.createElement("span", {
    className: "plk-chip ok"
  }, "current")), /*#__PURE__*/React.createElement("div", {
    className: "dl"
  }, /*#__PURE__*/React.createElement("a", {
    className: "territory-toggle xs",
    href: r.url,
    download: true,
    target: "_blank",
    rel: "noreferrer"
  }, "Download mp4"), r.posterUrl ? /*#__PURE__*/React.createElement("a", {
    className: "territory-toggle xs",
    href: r.posterUrl,
    download: true,
    target: "_blank",
    rel: "noreferrer"
  }, "Poster") : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs",
    onClick: () => copyText(r.url)
  }, "Copy link"), stale(r) && onRetry ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs",
    disabled: busy,
    onClick: () => onRetry([r.format])
  }, "Render again") : null), r.assetId ? /*#__PURE__*/React.createElement("div", {
    className: "dl hooks"
  }, /*#__PURE__*/React.createElement(Link, {
    className: "territory-toggle xs",
    title: "Cut and caption, trim or extend, find clips, resize for channels; every edit is kept as a version",
    to: `/Content/clips/${encodeURIComponent(String(r.assetId))}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "movie_edit"), "Edit in clip workspace")) : null))), /*#__PURE__*/React.createElement("div", {
    className: "sq-send"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sq-eyebrow"
  }, "Send to"), /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, sequence.containerId ? /*#__PURE__*/React.createElement(Button, {
    themeColor: "primary",
    size: "small",
    disabled: busy || attachBusy || !list.some(r => r.assetId),
    title: "Link every finished output to the piece this sequence belongs to; Ship on the piece then offers the video targets",
    onClick: () => onAttach && onAttach(list)
  }, attachBusy ? "Attaching…" : "Attach to piece") : /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, "Not attached to a piece: outputs live in Assets as one video with a version per format. Ship them from the piece they belong to."), list.some(r => r.assetId) ? /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, "Each output opens in the clip workspace, where its edits are kept as versions.") : /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, "Outputs register in Assets when the worker finishes uploading."))));
}

/** Every render on the sequence, newest first. */
export function RendersTable({
  sequence,
  renders,
  onRetry,
  busy
}) {
  const rows = [...(renders || [])].sort((a, b) => String(b.createdDate || "").localeCompare(String(a.createdDate || "")));
  if (!rows.length) return null;
  const stale = r => sequence.scriptVersion && r.scriptVersion && r.scriptVersion < sequence.scriptVersion;
  return /*#__PURE__*/React.createElement("div", {
    className: "sq-tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "sq-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "When"), /*#__PURE__*/React.createElement("th", null, "Format"), /*#__PURE__*/React.createElement("th", null, "Version"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Stage"), /*#__PURE__*/React.createElement("th", null))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
    key: r.id
  }, /*#__PURE__*/React.createElement("td", null, api.fmtDate(r.createdDate)), /*#__PURE__*/React.createElement("td", null, r.format), /*#__PURE__*/React.createElement("td", null, "v", r.scriptVersion || "?", stale(r) && r.status === "done" ? /*#__PURE__*/React.createElement("span", {
    className: "plk-chip warn",
    style: {
      marginLeft: 6
    }
  }, "older than the storyboard") : null), /*#__PURE__*/React.createElement("td", null, statusChip(r)), /*#__PURE__*/React.createElement("td", {
    className: "dim"
  }, r.status === "failed" ? r.error ? String(r.error).slice(0, 80) : r.stage || "failed" : r.status === "done" ? `${api.fmtClock(r.durationSeconds)} · ${elapsed(r)}` : `${r.stage || stageText(r)} · ${r.progress}%`), /*#__PURE__*/React.createElement("td", {
    className: "acts"
  }, r.status === "done" && r.url ? /*#__PURE__*/React.createElement("a", {
    className: "territory-toggle xs",
    href: r.url,
    target: "_blank",
    rel: "noreferrer"
  }, "Open") : null, r.status === "failed" && onRetry ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs",
    disabled: busy,
    onClick: () => onRetry([r.format])
  }, "Retry") : null))))));
}

/* ===================================================================== Exports tab (2026-10-08 redesign)
 * One card per size: ready (Download, click to watch it on the stage), rendering (stage in words + progress),
 * waiting, or failed (a plain reason + Retry). The watched card opens its tools: Copy link, Poster, Render again
 * when older than the storyboard, Edit in the clip workspace and the clip workspace hooks (Trim, Find clips,
 * Resize, Cut and caption). Attach to the piece when the sequence belongs to one. Publishing to social channels is
 * not offered here: the App ships from a piece (Builder › Ship), so attaching is the path.
 */
const STAGE_SENTENCE = {
  prep: "Getting the pictures ready",
  narration: "Adding the voice",
  assemble: "Putting the scenes together",
  music: "Adding the music",
  upload: "Saving the file"
};
const SIZE_NAME = {
  "16:9": "Wide 16:9",
  "9:16": "Story 9:16",
  "1:1": "Square 1:1"
};
export const exportName = f => SIZE_NAME[f] || f;
const stageSentence = r => {
  const s = String(r && r.stage || "").toLowerCase();
  return STAGE_SENTENCE[s] || "Rendering";
};
const plainReason = r => {
  const e = String(r && r.error || "").toLowerCase();
  if (/credit/.test(e)) return "There were not enough credits.";
  if (/timeout|timed out/.test(e)) return "It took too long.";
  if (/media|download|404|not found|missing/.test(e)) return "A photo or video in a scene could not be loaded.";
  if (/voice|narration|tts|polly/.test(e) || String(r && r.stage).toLowerCase() === "narration") return "The voice could not be made.";
  if (/music/.test(e) || String(r && r.stage).toLowerCase() === "music") return "The music file could not be used.";
  return `Something went wrong${r && r.stage ? ` while ${stageSentence(r).toLowerCase()}` : ""}.`;
};
const HOOKS = [["edit", "Cut and caption"], ["trim", "Trim"], ["clips", "Find clips"], ["resize", "Resize"]];
export function ExportsPanel({
  sequence,
  renders,
  busy,
  attachBusy,
  onRetry,
  onAttach,
  onHook,
  watchId,
  onWatch
}) {
  const batch = latestBatch(renders);
  const doneFormats = (renders || []).filter(r => r.status === "done" && r.url).map(r => r.format);
  const formats = [...sequence.formats, ...doneFormats.filter((f, i) => !sequence.formats.includes(f) && doneFormats.indexOf(f) === i)];
  const readyList = latestDonePerFormat(renders, formats).filter(Boolean);
  const stale = r => sequence.scriptVersion && r.scriptVersion && r.scriptVersion < sequence.scriptVersion;
  const inflight = batch.some(api.inFlight);
  return /*#__PURE__*/React.createElement("div", {
    className: "sx-exports"
  }, formats.map(f => {
    const cur = batch.find(r => r.format === f) || null;
    const done = latestDonePerFormat(renders, [f])[0];
    const running = cur && api.inFlight(cur);
    const failed = !!cur && cur.status === "failed";
    const watched = done && watchId === done.id;
    let line;
    let tone = "";
    if (running && cur.status === "queued") line = "Waiting its turn";else if (running) line = `${stageSentence(cur)} · ${cur.progress}%`;else if (failed) {
      line = `Did not finish. ${plainReason(cur)}`;
      tone = " bad";
    } else if (done) {
      line = `Ready · ${done.width ? `${done.width} × ${done.height}` : api.formatDims(f)} · ${api.fmtClock(done.durationSeconds)}`;
      tone = " ok";
    } else line = sequence.formats.includes(f) ? "Not rendered yet" : "Not in the sizes to render";
    return /*#__PURE__*/React.createElement("div", {
      key: f,
      className: "sx-ex" + (watched ? " sel" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pick",
      disabled: !done,
      "aria-pressed": !!watched,
      title: done ? "Watch it on the stage" : "",
      onClick: () => onWatch(watched ? null : done)
    }, /*#__PURE__*/React.createElement("span", {
      className: "th f" + f.replace(":", "")
    }, done && done.posterUrl ? /*#__PURE__*/React.createElement("img", {
      src: done.posterUrl,
      alt: "",
      loading: "lazy"
    }) : /*#__PURE__*/React.createElement("i", null)), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, exportName(f)), /*#__PURE__*/React.createElement("span", {
      className: "st" + tone,
      title: failed && cur.error ? String(cur.error).slice(0, 300) : undefined
    }, line), done && (running || failed) ? /*#__PURE__*/React.createElement("span", {
      className: "st"
    }, "The last finished one is still here") : null)), done ? /*#__PURE__*/React.createElement("a", {
      className: "sx-btn sm",
      href: done.url,
      download: true,
      target: "_blank",
      rel: "noreferrer"
    }, "Download") : null), running ? /*#__PURE__*/React.createElement("div", {
      className: "sx-meter",
      role: "progressbar",
      "aria-valuenow": cur.progress,
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-label": `${exportName(f)} progress`
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${cur.status === "queued" ? 2 : Math.max(4, cur.progress)}%`
      }
    })) : null, failed && onRetry ? /*#__PURE__*/React.createElement("div", {
      className: "acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn sm",
      disabled: busy || inflight,
      onClick: () => onRetry([f])
    }, "Retry")) : null, done && stale(done) && !running ? /*#__PURE__*/React.createElement("div", {
      className: "acts"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sx-tag warn"
    }, "Older than the storyboard"), onRetry ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn sm",
      disabled: busy || inflight,
      onClick: () => onRetry([f])
    }, "Render again") : null) : null, watched ? /*#__PURE__*/React.createElement("div", {
      className: "acts tools"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-btn sm",
      onClick: () => copyText(done.url)
    }, "Copy link"), done.posterUrl ? /*#__PURE__*/React.createElement("a", {
      className: "sx-btn sm",
      href: done.posterUrl,
      download: true,
      target: "_blank",
      rel: "noreferrer"
    }, "Poster") : null, done.assetId ? /*#__PURE__*/React.createElement(Link, {
      className: "sx-btn sm",
      to: `/Content/clips/${encodeURIComponent(String(done.assetId))}`,
      title: "Every edit is kept as a version"
    }, "Open in the clip editor") : null, done.assetId && onHook ? HOOKS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "sx-btn sm",
      onClick: () => onHook(k, assetOf(done, sequence))
    }, l)) : null) : null);
  }), /*#__PURE__*/React.createElement("div", {
    className: "sx-when"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sx-label"
  }, "When they are ready"), sequence.containerId ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "sx-wide",
    disabled: busy || attachBusy || !readyList.some(r => r.assetId),
    title: "Link every finished size to the piece this sequence belongs to; ship them from the piece",
    onClick: () => onAttach && onAttach(readyList)
  }, attachBusy ? "Adding…" : `Add ${readyList.length > 1 ? `all ${readyList.length}` : "it"} to the piece`) : /*#__PURE__*/React.createElement("span", {
    className: "sx-mute"
  }, "Each finished size is saved in Assets as a video. To publish, add it to a piece and ship it from there."), /*#__PURE__*/React.createElement("span", {
    className: "sx-mute"
  }, "You can leave this page; rendering keeps going and the files appear here when you come back.")), (renders || []).length ? /*#__PURE__*/React.createElement("details", {
    className: "sx-hist"
  }, /*#__PURE__*/React.createElement("summary", null, "Every render on this sequence"), /*#__PURE__*/React.createElement(RendersTable, {
    sequence: sequence,
    renders: renders,
    busy: busy || inflight,
    onRetry: onRetry
  })) : null);
}

/** Assets tab › Sequences: GET /sequences summaries (title, purpose, formats, last render per format, modified). */
export class SequencesList extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      items: undefined
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "load", async () => {
      let items = null;
      try {
        items = await api.listSequences({
          $top: 60
        });
      } catch (e) {
        toast(e && e.message || "Could not load sequences.");
        items = [];
      }
      if (this._alive) this.setState({
        items
      });
    });
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  lastRenderChip(s, f) {
    const lr = s.lastRender;
    const r = lr && (Array.isArray(lr) ? lr.find(x => x && x.format === f) : lr[f]);
    if (!r) return /*#__PURE__*/React.createElement("span", {
      key: f,
      className: "plk-chip",
      title: "not rendered yet"
    }, f);
    const st = String(r.status || "").toLowerCase();
    const cls = st === "done" ? " ok" : st === "failed" ? " bad" : api.inFlight({
      status: st
    }) ? " info" : "";
    return /*#__PURE__*/React.createElement("span", {
      key: f,
      className: "plk-chip" + cls,
      title: `${f}: ${st}${r.scriptVersion ? ` · v${r.scriptVersion}` : ""}${r.finishedDate ? ` · ${api.fmtDate(r.finishedDate)}` : ""}`
    }, f, st === "done" && s.scriptVersion && r.scriptVersion && r.scriptVersion < s.scriptVersion ? " · older" : st === "done" ? "" : ` · ${st}`);
  }
  render() {
    const {
      q,
      onOpen,
      onNew
    } = this.props;
    const {
      items
    } = this.state;
    const ql = String(q || "").trim().toLowerCase();
    if (items === undefined) return /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Loading sequences\u2026");
    if (items === null) return /*#__PURE__*/React.createElement("div", {
      className: "module-empty"
    }, "Sequences are not available on this server yet. The Create dialog's Sequence tab lights up with the same deploy.");
    const list = items.filter(s => !ql || `${s.title} ${s.purpose} ${s.brief}`.toLowerCase().includes(ql));
    if (!list.length) return /*#__PURE__*/React.createElement("div", {
      className: "module-empty"
    }, ql ? "No sequence matches." : "No sequences yet.", " ", onNew ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      style: {
        marginLeft: 8
      },
      onClick: onNew
    }, "Create a sequence") : null);
    return /*#__PURE__*/React.createElement("div", {
      className: "sq-list"
    }, list.map(s => {
      const st = s.status;
      const n = s.segmentCount !== undefined ? Number(s.segmentCount) : null;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: s.id,
        className: "sq-row",
        onClick: () => onOpen && onOpen(s.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "sq-row-main"
      }, /*#__PURE__*/React.createElement("b", null, s.title), /*#__PURE__*/React.createElement("span", {
        className: "sub"
      }, api.purposeLabel(s.purpose), n !== null ? ` · ${n} card${n === 1 ? "" : "s"}` : "", s.estimatedSeconds ? ` · ${api.fmtClock(s.estimatedSeconds)}` : "", s.scriptVersion ? ` · v${s.scriptVersion}` : "")), /*#__PURE__*/React.createElement("span", {
        className: "sq-row-fmts"
      }, s.formats.map(f => this.lastRenderChip(s, f))), /*#__PURE__*/React.createElement("span", {
        className: "sq-row-right"
      }, /*#__PURE__*/React.createElement("span", {
        className: "plk-chip" + (st === "ready" ? " ok" : st === "rendering" ? " info" : "")
      }, st), /*#__PURE__*/React.createElement("span", {
        className: "territory-hint"
      }, api.timeAgo(s.modifiedDate || s.createdDate))));
    }));
  }
}